import { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { weekAnchorDate } from '@/lib/cohortWeeks';
import { pickDefaultEnrollment } from '@/lib/cohortAccess';

export interface CohortEnrollment {
  cohort_id: string;
  cohort_name: string;
  program_id: string;
  program_name: string;
  start_date: string | null;
  /** Fecha desde la que se cuentan las semanas (ver weekAnchorDate). Usar esta,
   *  no start_date, para cualquier cálculo de "semana N". */
  week_start_date: string | null;
  end_date: string | null;
  demo_day_date: string | null;
  demo_day_link: string | null;
  demo_day_enabled: boolean;
  project_deadline: string | null;
  project_deadline_week: number | null;
  challenge_lab_deadline: string | null;
  challenge_lab_deadline_week: number | null;
  content_access_deadline: string | null;
  status: string | null;
  sequential_unlock: boolean;
  is_perpetual: boolean;
  plan_start_at: string | null;
  /** Cohorte visible solo por ser mentor: lectura, sin avance ni escrituras. */
  view_only?: boolean;
}

const STORAGE_KEY = 'selected_cohort_id';

export function useSelectedCohort() {
  const { user, roles } = useAuth();
  const isMentor = roles.includes('mentor');

  const [selectedCohortId, setSelectedCohortIdState] = useState<string | null>(
    () => localStorage.getItem(STORAGE_KEY)
  );


  const { data: ownEnrollments = [], isLoading } = useQuery({
    queryKey: ['all-cohort-enrollments', user?.id],
    queryFn: async (): Promise<CohortEnrollment[]> => {
      const { data, error } = await supabase
        .from('cohort_participants')
        .select(`
          cohort_id,
          plan_start_at,
            cohorts (
            id, name, start_date, end_date, demo_day_date, demo_day_link, demo_day_enabled, project_deadline, project_deadline_week, challenge_lab_deadline, challenge_lab_deadline_week, content_access_deadline, status, is_perpetual, first_session_after_start,
            programs ( id, name, sequential_unlock )
          )
        `)
        .eq('user_id', user!.id);

      if (error) throw error;
      if (!data) return [];

      return data.map((row: any) => ({
        cohort_id: row.cohort_id,
        cohort_name: row.cohorts?.name ?? '',
        program_id: row.cohorts?.programs?.id ?? '',
        program_name: row.cohorts?.programs?.name ?? '',
        start_date: row.cohorts?.start_date ?? null,
        week_start_date: weekAnchorDate(row.cohorts?.start_date, row.cohorts?.first_session_after_start),
        end_date: row.cohorts?.end_date ?? null,
        demo_day_date: row.cohorts?.demo_day_date ?? null,
        demo_day_link: row.cohorts?.demo_day_link ?? null,
        demo_day_enabled: row.cohorts?.demo_day_enabled ?? true,
        project_deadline: row.cohorts?.project_deadline ?? null,
        project_deadline_week: row.cohorts?.project_deadline_week ?? null,
        challenge_lab_deadline: row.cohorts?.challenge_lab_deadline ?? null,
        challenge_lab_deadline_week: row.cohorts?.challenge_lab_deadline_week ?? null,
        content_access_deadline: row.cohorts?.content_access_deadline ?? null,
        status: row.cohorts?.status ?? null,
        sequential_unlock: row.cohorts?.programs?.sequential_unlock ?? false,
        is_perpetual: row.cohorts?.is_perpetual ?? false,
        plan_start_at: row.plan_start_at ?? null,
      }));
    },
    enabled: !!user?.id,
  });

  // Cohortes donde el usuario es mentor: solo lectura. No generan inscripción
  // ni avance; sirven para poder revisar contenido/calendario de esa cohorte.
  const { data: mentorCohorts = [] } = useQuery({
    queryKey: ['mentor-view-cohorts', user?.id],
    queryFn: async (): Promise<CohortEnrollment[]> => {
      const { data, error } = await supabase
        .from('cohort_mentors')
        .select(`
          cohort_id,
          cohorts (
            id, name, start_date, end_date, demo_day_date, demo_day_link, demo_day_enabled, project_deadline, project_deadline_week, challenge_lab_deadline, challenge_lab_deadline_week, content_access_deadline, status, is_perpetual, first_session_after_start,
            programs ( id, name, sequential_unlock )
          )
        `)
        .eq('user_id', user!.id);

      if (error) throw error;
      return (data ?? [])
        .filter((row: any) => !!row.cohorts)
        .map((row: any) => ({
          cohort_id: row.cohort_id,
          cohort_name: row.cohorts?.name ?? '',
          program_id: row.cohorts?.programs?.id ?? '',
          program_name: row.cohorts?.programs?.name ?? '',
          start_date: row.cohorts?.start_date ?? null,
          week_start_date: weekAnchorDate(row.cohorts?.start_date, row.cohorts?.first_session_after_start),
          end_date: row.cohorts?.end_date ?? null,
          demo_day_date: row.cohorts?.demo_day_date ?? null,
          demo_day_link: row.cohorts?.demo_day_link ?? null,
          demo_day_enabled: row.cohorts?.demo_day_enabled ?? true,
          project_deadline: row.cohorts?.project_deadline ?? null,
          project_deadline_week: row.cohorts?.project_deadline_week ?? null,
          challenge_lab_deadline: row.cohorts?.challenge_lab_deadline ?? null,
          challenge_lab_deadline_week: row.cohorts?.challenge_lab_deadline_week ?? null,
          content_access_deadline: row.cohorts?.content_access_deadline ?? null,
          status: row.cohorts?.status ?? null,
          sequential_unlock: row.cohorts?.programs?.sequential_unlock ?? false,
          is_perpetual: row.cohorts?.is_perpetual ?? false,
          plan_start_at: null,
          view_only: true,
        }));
    },
    enabled: !!user?.id && isMentor,
  });

  // La inscripción real siempre gana sobre la vista de mentor.
  const enrollments = useMemo<CohortEnrollment[]>(() => {
    const ownIds = new Set(ownEnrollments.map(e => e.cohort_id));
    return [...ownEnrollments, ...mentorCohorts.filter(c => !ownIds.has(c.cohort_id))];
  }, [ownEnrollments, mentorCohorts]);

  // Auto-select first enrollment if nothing selected or selection invalid
  const activeEnrollment = useMemo(() => {
    if (enrollments.length === 0) return null;
    const found = enrollments.find(e => e.cohort_id === selectedCohortId);
    if (found) return found;
    return pickDefaultEnrollment(enrollments);
  }, [enrollments, selectedCohortId]);


  const setSelectedCohortId = useCallback((id: string) => {
    localStorage.setItem(STORAGE_KEY, id);
    setSelectedCohortIdState(id);
  }, []);

  return {
    enrollments,
    isLoading,
    selectedCohortId: activeEnrollment?.cohort_id ?? null,
    selectedProgramId: activeEnrollment?.program_id ?? null,
    activeEnrollment,
    /** true cuando la cohorte seleccionada se ve por ser mentor (solo lectura) */
    isViewOnly: !!activeEnrollment?.view_only,
    /** true si el usuario eligió esta cohorte a mano (guardada) */
    isManualSelection: !!activeEnrollment && activeEnrollment.cohort_id === selectedCohortId,
    setSelectedCohortId,
  };
}
